import { t } from "@lingui/macro";
import {
  NavigationProp,
  StackActions,
  useNavigation,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback, useMemo } from "react";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { selectedRoundAtom, selectedRoundIdAtom } from "../../atoms/stores";
import { useCustomerJoinNonPrePaidBooking } from "../useCustomerJoinNotPrePaidBooking/useCustomerJoinNonPrePaidBooking";
import { useCustomerJoinPaidBooking } from "../useCustomerJoinPaidBooking/useCustomerJoinPaidBooking";
import { useMemberJoinNonPrePaidBooking } from "../useMemberJoinNotPrePaidBooking/useMemberJoinNonPrePaidBooking";
import { useJoinBooking_Booking$key } from "./__generated__/useJoinBooking_Booking.graphql";

export const useJoinBooking = ({
  bookingRef,
}: {
  bookingRef: useJoinBooking_Booking$key | null;
}) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const booking = useFragment(useJoinBookingFragment, bookingRef);
  const { showToast } = useToast();
  const shareId = booking?.shareId;

  const trackEvent = useTrackEvent();

  const hasMemberRounds =
    booking?.rounds?.edges?.some(
      (edge) => edge?.node?.__typename === "MemberRound"
    ) ?? false;
  const isPrivateClub =
    booking?.startSlot?.agenda?.facility?.club?.visibility === "PRIVATE";
  const isPrivateInviteFlow = isPrivateClub && hasMemberRounds;
  const requiresPrePayment =
    !isPrivateClub &&
    booking?.startSlot?.agenda?.facility?.publicBookingPermissionSet
      ?.prePaymentRequirement === "FULL";
  const { handleCustomerJoinPaidBooking, isJoiningPaidBooking } =
    useCustomerJoinPaidBooking();
  const { handleCustomerJoinNonPrePaidBooking, isJoiningNonPrePaidBooking } =
    useCustomerJoinNonPrePaidBooking();
  const {
    handleMemberJoinNonPrePaidBooking,
    isJoiningNonPrePaidBooking: isJoiningMemberNonPrePaidBooking,
  } = useMemberJoinNonPrePaidBooking();

  //Sometimes I just like fun...
  const toastDescriptions = useMemo(
    () => [
      t`Ah, it just feels so good to make plans.`,
      t`Oh Boy! I love having things scheduled.`,
      t`Kerblam! Another one on the books.`,
      t`I have it on good faith that this is going to be the best booking ever joined.`,
    ],
    []
  );

  const handleJoinSuccess = useCallback(
    (slotOrBookingId: string) => {
      trackEvent("JoinBooking.BookingJoined", { bookingId: slotOrBookingId });
      //Show toast and navigate to the booking summary screen.
      showToast({
        title: t`Booking Joined`,
        description:
          toastDescriptions[
            Math.floor(Math.random() * toastDescriptions.length)
          ],
        semanticStyle: "success",
        toastId: "paid-booking-joined-toast",
      });
      navigation.dispatch(
        StackActions.replace("BookingSummary", { slotOrBookingId })
      );
    },
    [navigation, showToast, toastDescriptions, trackEvent]
  );

  const handleJoinError = useCallback(() => {
    showToast({
      title: t`Booking Joined`,
      description: t`Oops! We couldn't join the booking. Please try again.`,
      semanticStyle: "error",
    });
  }, [showToast]);

  /**
   * Temporarily only handling join specific round.
   * Will extend to handling join specific OR configure round
   * later.
   */
  const roundId = useAtomValue(selectedRoundIdAtom);
  const round = useAtomValue(selectedRoundAtom);
  const nonPrePaidRound = useMemo(
    () => ({
      rateId: round?.rate?.id ?? undefined,
      courseLayoutId: round?.courseLayoutId,
      playerId: round?.playerId,
      transportationTypeId: round?.transportationTypeId,
    }),
    [
      round?.rate?.id,
      round?.courseLayoutId,
      round?.playerId,
      round?.transportationTypeId,
    ]
  );

  const handleJoinBooking = useCallback(() => {
    if (
      shareId &&
      !requiresPrePayment &&
      nonPrePaidRound &&
      isPrivateInviteFlow
    ) {
      handleMemberJoinNonPrePaidBooking(
        {
          shareId,
          round: nonPrePaidRound,
        },
        handleJoinSuccess,
        handleJoinError
      );
    } else if (!requiresPrePayment && shareId && nonPrePaidRound) {
      handleCustomerJoinNonPrePaidBooking(
        {
          shareId,
          round: nonPrePaidRound,
        },
        handleJoinSuccess,
        handleJoinError
      );
    } else if (roundId && shareId && !isPrivateInviteFlow) {
      handleCustomerJoinPaidBooking(
        {
          shareId,
          roundId,
        },
        handleJoinSuccess,
        handleJoinError
      );
    }
  }, [
    roundId,
    shareId,
    handleCustomerJoinPaidBooking,
    handleJoinSuccess,
    handleJoinError,
    handleCustomerJoinNonPrePaidBooking,
    handleMemberJoinNonPrePaidBooking,
    requiresPrePayment,
    nonPrePaidRound,
    isPrivateInviteFlow,
  ]);

  const isJoining =
    isJoiningPaidBooking ||
    isJoiningNonPrePaidBooking ||
    isJoiningMemberNonPrePaidBooking;

  return { handleJoinBooking, isJoining };
};

const useJoinBookingFragment = graphql`
  fragment useJoinBooking_Booking on Booking {
    id
    shareId
    startSlot {
      agenda {
        facility {
          club {
            visibility
          }
          publicBookingPermissionSet {
            prePaymentRequirement
          }
        }
      }
    }
    rounds {
      edges {
        node {
          id
          __typename
          player {
            id
          }
        }
      }
    }
  }
`;
